import { Component, computed, DestroyRef, inject, input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { toast } from 'ngx-sonner';
import { v7 as uuidv7 } from 'uuid';

import { Cartographie } from '../../../cartographie/models/cartographie.model';
import { FormControlNames } from '../../../référentiel-métier-parcours/models/form-control-names.model';
import { ButtonAddNewComponent } from '../../../shared/components/button-add-new/button-add-new.component';
import { SidepanelService } from '../../../shared/components/sidepanel/sidepanel.service';
import { ToastrSuccessComponent } from '../../../shared/components/toastr/toastr-success.component';
import { Phase } from '../../référentiel-métier-processus/processus-phases/models/phase.model';
import { PhaseStore } from '../../référentiel-métier-processus/processus-phases/store/phase.store';
import { InterrelationGroupComponent } from '../interrelation-group/interrelation-group.component';
import { Interrelation } from '../models/interrelation.model';
import { NouvelleInterrelation } from '../requests/create-request';
import { InterrelationAModifier } from '../requests/update-request';
import { InterrelationStore } from '../store/interrelation.store';
import { Référentiel } from '../../../administration/gestion-référentiels/models/référentiel.model';

@Component({
  selector: 'app-form-interrelation',
  imports: [InterrelationGroupComponent, ButtonAddNewComponent],
  templateUrl: './form-interrelations.component.html',
})
export class FormInterrelationsComponent implements OnInit {
  readonly #fb = inject(FormBuilder);
  readonly #interrelationStore = inject(InterrelationStore);
  readonly #phaseStore = inject(PhaseStore);
  readonly #sidepanelService = inject(SidepanelService);
  readonly #destroyRef = inject(DestroyRef);
  readonly phases = computed(() => this.#phaseStore.entities());
  readonly interrelations = computed(() => this.#interrelationStore.entities());

  readonly referentiel = input.required<Référentiel>();
  readonly cartographie = input.required<Cartographie>();

  form = this.#fb.group({
    [FormControlNames.INTERRELATIONS]: this.#fb.array([], Validators.minLength(1)),
  });

  ngOnInit(): void {
    this.ajouterInterrelation();
    this.#sidepanelService.submit.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => this.onSubmit());
  }

  ajouterInterrelation() {
    const interrelationForm = this.#fb.group({
      interrelation: [],
      phases: [[], Validators.required],
    });
    this.interrelationsForm.push(interrelationForm);
  }

  onSubmit() {
    const toastrId = uuidv7();

    const interrelations = (this.interrelationsForm.value as { interrelation: Interrelation; phases: Phase[] }[])
      .filter((x) => !!x.interrelation?.nom.trim())
      .map((form) => ({
        nom: form.interrelation.nom,
        idsPhase: form.phases.map((x) => x.id),
        idReferentiel: this.referentiel().id,
      }));

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const interrelationsACréer: NouvelleInterrelation[] = [];
    const interrelationsAModifier: InterrelationAModifier[] = [];

    interrelations.forEach((interrelation) => {
      const interrelationExistante = this.#interrelationStore.entities().find((x) => x.nom == interrelation.nom);
      if (interrelationExistante) {
        interrelationsAModifier.push({
          nom: interrelation.nom,
          idsPhase: interrelation.idsPhase,
          id: interrelationExistante.id,
        });
      } else {
        interrelationsACréer.push({
          nom: interrelation.nom,
          idsPhase: interrelation.idsPhase,
        });
      }
    });

    if (interrelationsAModifier.length > 0) {
      this.#interrelationStore.modifier({
        data: {
          idReferentiel: this.referentiel().id,
          interrelations: interrelationsAModifier,
        },
        next: () => {
          this.#next(toastrId);
        },
      });
    }

    if (interrelationsACréer.length > 0) {
      this.#interrelationStore.créer({
        data: {
          idReferentiel: this.referentiel().id,
          interrelations: interrelationsACréer,
        },
        next: () => this.#next(toastrId),
      });
    }

    this.#sidepanelService.close();
  }

  #next(toastrId: string) {
    toast.custom(ToastrSuccessComponent, {
      componentProps: {
        message: 'Association interrelation/phases réussie',
        id: toastrId,
      },
      id: toastrId,
    });
    this.#interrelationStore.chargerToutes(this.cartographie().id);
    this.#phaseStore.charger(this.referentiel().id);
  }

  get interrelationsForm() {
    return this.form.get(FormControlNames.INTERRELATIONS) as FormArray<FormGroup>;
  }
}
